import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useRegistration } from '../../context/RegistrationContext';
import { useModal } from '../../context/ModalContext';
import { Button } from '../../components/common/Button';
import Timer from '../../components/common/Timer';
import { createRezervacia } from '../../api/letavyApi';

const ReservationPage = () => {
  const { state, dispatch, actionTypes } = useRegistration();
  const { confirmAction } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);

  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => {
      if (!errorRef.current) return;
      errorRef.current.focus({ preventScroll: true });
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => clearTimeout(id);
  }, [error]);

  const { selectedDielna, rezervacia, timeLeft } = state;
  
  // Ak je časovač aktívny a vyprší, vrátime sa na výber dielne
  useEffect(() => {
    if (rezervacia && timeLeft <= 0) {
      dispatch({ type: actionTypes.SET_ERROR, payload: 'Vypršal čas na rezerváciu. Prosím, vyberte si dielňu znova.' });
      dispatch({ type: actionTypes.SET_REZERVACIA, payload: null });
      dispatch({ type: actionTypes.SET_STEP, payload: 3 });
    }
  }, [rezervacia, timeLeft, dispatch, actionTypes]);
  
  // Vytváranie rezervácie
  useEffect(() => {
    // Pomocný flag na sledovanie, či sa už vykonalo volanie API
    let hasAttemptedReservation = false;
    
    const makeReservation = async () => {
      // Zabránenie opakovanému volaniu API
      if (hasAttemptedReservation) return;
      
      if (selectedDielna && !rezervacia && !isLoading && !error) {
        try {
          hasAttemptedReservation = true;
          setIsLoading(true);
          setError(null);
          
          const response = await createRezervacia(selectedDielna.id);
          
          if (response && response.stav === 'ok') {
            dispatch({ type: actionTypes.SET_REZERVACIA, payload: response.rezervacia });
            dispatch({ type: actionTypes.UPDATE_TIME_LEFT, payload: 120 }); // Nastavenie časovača na 120 sekúnd (2 minúty)
          } else {
            setError(response?.error || response?.err || 'Nepodarilo sa vytvoriť rezerváciu.');
          }
        } catch (err) {
          console.error('Chyba pri vytváraní rezervácie:', err);
          setError('Nepodarilo sa vytvoriť rezerváciu. Skúste to prosím neskôr.');
          
          // Pri CORS alebo sieťovej chybe presmerujeme späť na výber dielne
          if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
            setTimeout(() => {
              dispatch({ type: actionTypes.SET_STEP, payload: 3 });
            }, 3000); // Krátke oneskorenie pre zobrazenie chybovej správy
          }
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    makeReservation();
    
    // Cleanup funkcia pre useEffect
    return () => {
      hasAttemptedReservation = true; // Zaistí, že sa volanie nevykoná znova
    };
  }, [selectedDielna, rezervacia, isLoading, error, dispatch, actionTypes]);
  
  const handleBack = () => {
    // Kompletne vyčistíme stav rezervácie, aby sme mohli vytvoriť novú
    dispatch({ type: actionTypes.SET_REZERVACIA, payload: null });
    dispatch({ type: actionTypes.SET_SELECTED_DIELNA, payload: null });
    dispatch({ type: actionTypes.UPDATE_TIME_LEFT, payload: 0 });
    dispatch({ type: actionTypes.SET_ERROR, payload: null });
    // Vrátime sa na výber dielne
    dispatch({ type: actionTypes.SET_STEP, payload: 3 });
  };
  
  const handleContinue = () => {
    if (rezervacia) {
      // Aktualizácia formulárových dát s informáciami o dielni
      dispatch({ 
        type: actionTypes.UPDATE_FORM_DATA, 
        payload: { 
          dielna_id: selectedDielna.id,
          dielna: selectedDielna.nazov,
          rezervacia: rezervacia.uuid 
        } 
      });
      dispatch({ type: actionTypes.SET_STEP, payload: 5 });
    }
  };
  
  const handleRestart = () => {
    confirmAction(
      'Začať odznova', 
      'Skutočne chcete začať registráciu odznova? Všetky doteraz zadané údaje vrátane rezervácie budú stratené.', 
      () => {
        dispatch({ type: actionTypes.RESET_STATE });
        dispatch({ type: actionTypes.SET_STEP, payload: 1 });
      },
      { confirmText: 'Začať odznova', variant: 'warning' }
    );
  };

  return (
    <motion.div 
      className="container mx-auto px-4 py-8 max-w-3xl"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-6">
          Rezervácia miesta v dielni
        </h1>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="ml-3">Vytvárame rezerváciu...</p>
          </div>
        ) : error ? (
          <div
            ref={errorRef}
            tabIndex={-1}
            className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 outline-none"
          >
            <p className="text-red-700">{error}</p>
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={handleBack}
              >
                Späť na výber dielne
              </Button>
            </div>
          </div>
        ) : rezervacia ? (
          <>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
              <p className="text-green-700 font-semibold">
                Rezervácia úspešne vytvorená!
              </p>
              <p className="text-green-700">
                Pre dokončenie registrácie pokračujte ďalej.
              </p>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-heading font-semibold text-primary mb-3">
                Detaily rezervácie:
              </h2>
              
              <div className="bg-background p-4 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <p className="text-sm text-gray-600">Dielňa:</p>
                    <p className="font-medium">{selectedDielna.nazov}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Rezervačné ID:</p>
                    <p className="font-medium">{rezervacia.uuid}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <Timer />
            
            <p className="text-sm text-gray-600 mb-6">
              Vaša rezervácia je platná po dobu vyššie uvedeného času. 
              Po vypršaní časového limitu bude rezervácia automaticky zrušená.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => confirmAction(
                    'Zrušenie rezervácie', 
                    'Naozaj chcete zrušiť svoju rezerváciu v dielni? Ak rezerváciu zrušíte, budete sa musieť vrátiť na výber dielne.', 
                    handleBack,
                    { confirmText: 'Zrušiť rezerváciu', variant: 'danger' }
                  )}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Zrušiť rezerváciu
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleRestart}
                  className="border-accent text-accent hover:bg-accent/10 hover:text-accent focus-visible:ring-accent"
                >
                  Začať odznova
                </Button>
              </div>
              
              <Button 
                variant="accent"
                onClick={handleContinue}
              >
                Pokračovať na registráciu
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">Pripravujeme rezerváciu...</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ReservationPage;
